"use client";

import { useState, useCallback, useRef, useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/app/components/ui/card";
import { Button } from "@/app/components/ui/button";
import { Input } from "@/app/components/ui/input";
import { Label } from "@/app/components/ui/label";
import { Badge } from "@/app/components/ui/badge";
import { Alert, AlertDescription } from "@/app/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/app/components/ui/tabs";
import { Textarea } from "@/app/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/app/components/ui/select";
import { Slider } from "@/app/components/ui/slider";
import { Checkbox } from "@/app/components/ui/checkbox";
import { CopyButton } from "@/app/components/ui/copy-button";
import { useMetadata } from "./useMetadata";
import { metadataService } from "@/lib/metadata/metadata-service";
import { CanonicalMetadata, MetadataValidationResult, validateCanonicalMetadata } from "@/lib/metadata/canonical-schema";
import { 
  Upload, 
  FileImage, 
  Music, 
  Eye, 
  Edit, 
  Settings, 
  AlertTriangle, 
  Info, 
  Star, 
  Tag, 
  Camera, 
  MapPin, 
  Calendar, 
  User, 
  Clock, 
  Hash, 
  Globe, 
  Save, 
  X, 
  Download, 
  Zap, 
  FileText, 
  BarChart3, 
  Search, 
  Shield,
  History as HistoryIcon,
  Plus,
  Minus,
  Copy,
  Trash2,
  RefreshCw,
  Filter,
  SortAsc,
  SortDesc,
  Grid,
  List,
  FileUp,
  FileDown,
  CheckCircle,
  XCircle,
  Loader2,
  Database,
  Layers,
  Target,
  Wand2,
  BookOpen,
  Lightbulb,
  TrendingUp,
  PieChart,
  BarChart,
  Activity
} from "lucide-react";

// Type definitions
interface MetadataField {
  key: string;
  value: string;
  label: string;
  type: string;
  editable: boolean;
  options?: string[];
  required?: boolean;
  validation?: {
    min?: number;
    max?: number;
    pattern?: string;
    message?: string;
  };
  description?: string;
  category?: string;
  priority?: number;
}

interface MetadataCategory {
  [key: string]: MetadataField[];
}

interface MetadataTemplate {
  title: string;
  description: string;
  icon: string;
  fields: {
    [key: string]: MetadataField[];
  };
}

interface MetadataData {
  fileName: string;
  fileSize: string;
  fileType: string;
  lastModified: string;
  fileHash?: string;
  [key: string]: MetadataField[] | string | undefined;
}

interface HistoryItem {
  id: string;
  fileName: string;
  timestamp: Date;
  metadata: string;
  fileSize: string;
  fileType: string;
}

interface BatchFile {
  id: string;
  file: File;
  metadata: MetadataData | null;
  status: 'pending' | 'processing' | 'completed' | 'error';
  error?: string;
}

interface MetadataStats {
  totalFiles: number;
  totalSize: number;
  fileTypes: Record<string, number>;
  commonTags: Record<string, number>;
  averageFileSize: number;
  lastProcessed: Date | null;
}

interface ValidationResult {
  isValid: boolean;
  errors: Array<{
    field: string;
    message: string;
    severity: 'error' | 'warning' | 'info';
  }>;
  warnings: Array<{
    field: string;
    message: string;
  }>;
}

export default function MetadataEditorPage() {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [metadata, setMetadata] = useState<CanonicalMetadata | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState("basic");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [metadataHistory, setMetadataHistory] = useState<HistoryItem[]>([]);
  const [favorites, setFavorites] = useState<string[]>([]);
  const [batchFiles, setBatchFiles] = useState<BatchFile[]>([]);
  const [isBatchMode, setIsBatchMode] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [sortBy, setSortBy] = useState<'name' | 'date' | 'size' | 'type'>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [selectedTemplate, setSelectedTemplate] = useState<string>('auto');
  const [validationResult, setValidationResult] = useState<MetadataValidationResult | null>(null);
  const [metadataStats, setMetadataStats] = useState<MetadataStats | null>(null);
  const [showStats, setShowStats] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [customFields, setCustomFields] = useState<MetadataField[]>([]);
  const [showCustomFields, setShowCustomFields] = useState(false);
  const [currentAdapter, setCurrentAdapter] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const batchInputRef = useRef<HTMLInputElement>(null);

  // Helper function to get field category
  const getFieldCategory = (field: string): string => {
    const categories: Record<string, string> = {
      title: 'basic',
      description: 'basic',
      keywords: 'basic',
      creator: 'basic',
      copyright: 'basic',
      gps: 'geographic',
      rating: 'basic',
      colorLabel: 'basic',
      people: 'basic',
      album: 'media',
      show: 'media',
      artwork: 'media',
      technical: 'technical',
      provenance: 'workflow',
      language: 'basic',
      locale: 'basic',
      custom: 'custom'
    };
    return categories[field] || 'unknown';
  };

  // Advanced metadata templates
  const metadataTemplates: Record<string, MetadataTemplate> = {
    auto: {
      title: "Auto-Detect",
      description: "Automatically detect file type and apply appropriate template",
      icon: "Wand2",
      fields: {}
    },
    photography: {
      title: "Photography Template",
      description: "Professional photography metadata with EXIF data",
      icon: "Camera",
      fields: {
        basic: [
          { key: "title", value: "", label: "Title", type: "text", editable: true, required: true, description: "Main title of the image" },
          { key: "description", value: "", label: "Description", type: "textarea", editable: true, description: "Detailed description of the image content" },
          { key: "keywords", value: "", label: "Keywords", type: "text", editable: true, description: "Comma-separated keywords for searchability" },
          { key: "author", value: "", label: "Photographer", type: "text", editable: true, required: true, description: "Name of the photographer" },
          { key: "copyright", value: "", label: "Copyright", type: "text", editable: true, description: "Copyright information" },
          { key: "rating", value: "", label: "Rating", type: "select", editable: true, options: ["1", "2", "3", "4", "5"], description: "Image quality rating" },
          { key: "colorLabel", value: "", label: "Color Label", type: "select", editable: true, options: ["Red", "Yellow", "Green", "Blue", "Purple"], description: "Color-coded label for organization" },
        ],
        camera: [
          { key: "make", value: "", label: "Camera Make", type: "text", editable: true },
          { key: "model", value: "", label: "Camera Model", type: "text", editable: true },
          { key: "lens", value: "", label: "Lens", type: "text", editable: true },
          { key: "focalLength", value: "", label: "Focal Length", type: "text", editable: true },
          { key: "aperture", value: "", label: "Aperture", type: "text", editable: true },
          { key: "shutterSpeed", value: "", label: "Shutter Speed", type: "text", editable: true },
          { key: "iso", value: "", label: "ISO", type: "number", editable: true },
          { key: "flash", value: "", label: "Flash", type: "select", editable: true, options: ["Yes", "No", "Auto"] },
        ],
        location: [
          { key: "gpsLatitude", value: "", label: "GPS Latitude", type: "text", editable: true },
          { key: "gpsLongitude", value: "", label: "GPS Longitude", type: "text", editable: true },
          { key: "location", value: "", label: "Location", type: "text", editable: true },
          { key: "city", value: "", label: "City", type: "text", editable: true },
          { key: "country", value: "", label: "Country", type: "text", editable: true },
        ],
        technical: [
          { key: "width", value: "", label: "Width", type: "number", editable: false },
          { key: "height", value: "", label: "Height", type: "number", editable: false },
          { key: "orientation", value: "", label: "Orientation", type: "select", editable: true, options: ["Horizontal", "Vertical", "Square"] },
          { key: "colorSpace", value: "", label: "Color Space", type: "select", editable: true, options: ["sRGB", "Adobe RGB", "ProPhoto RGB"] },
          { key: "dateTaken", value: "", label: "Date Taken", type: "date", editable: true },
          { key: "software", value: "", label: "Software", type: "text", editable: true },
        ]
      }
    },
    music: {
      title: "Music Template",
      description: "Audio file metadata with ID3 tags and technical info",
      icon: "Music",
      fields: {
        basic: [
          { key: "title", value: "", label: "Title", type: "text", editable: true },
          { key: "artist", value: "", label: "Artist", type: "text", editable: true },
          { key: "album", value: "", label: "Album", type: "text", editable: true },
          { key: "genre", value: "", label: "Genre", type: "text", editable: true },
          { key: "year", value: "", label: "Year", type: "number", editable: true },
          { key: "track", value: "", label: "Track Number", type: "number", editable: true },
          { key: "totalTracks", value: "", label: "Total Tracks", type: "number", editable: true },
          { key: "composer", value: "", label: "Composer", type: "text", editable: true },
          { key: "lyricist", value: "", label: "Lyricist", type: "text", editable: true },
          { key: "comment", value: "", label: "Comment", type: "textarea", editable: true },
          { key: "copyright", value: "", label: "Copyright", type: "text", editable: true },
        ],
        audio: [
          { key: "bitrate", value: "", label: "Bitrate", type: "text", editable: false },
          { key: "sampleRate", value: "", label: "Sample Rate", type: "text", editable: false },
          { key: "channels", value: "", label: "Channels", type: "text", editable: false },
          { key: "duration", value: "", label: "Duration", type: "text", editable: false },
          { key: "format", value: "", label: "Format", type: "text", editable: false },
          { key: "bpm", value: "", label: "BPM", type: "number", editable: true },
          { key: "key", value: "", label: "Key", type: "select", editable: true, options: ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"] },
          { key: "mood", value: "", label: "Mood", type: "text", editable: true },
          { key: "rating", value: "", label: "Rating", type: "select", editable: true, options: ["1", "2", "3", "4", "5"] },
        ]
      }
    },
    video: {
      title: "Video Template",
      description: "Video file metadata with technical specifications",
      icon: "FileImage",
      fields: {
        basic: [
          { key: "title", value: "", label: "Title", type: "text", editable: true, required: true },
          { key: "description", value: "", label: "Description", type: "textarea", editable: true },
          { key: "director", value: "", label: "Director", type: "text", editable: true },
          { key: "producer", value: "", label: "Producer", type: "text", editable: true },
          { key: "cast", value: "", label: "Cast", type: "textarea", editable: true },
          { key: "genre", value: "", label: "Genre", type: "select", editable: true, options: ["Action", "Comedy", "Drama", "Horror", "Sci-Fi", "Documentary", "Animation"] },
          { key: "rating", value: "", label: "Content Rating", type: "select", editable: true, options: ["G", "PG", "PG-13", "R", "NC-17"] },
          { key: "year", value: "", label: "Release Year", type: "number", editable: true },
          { key: "duration", value: "", label: "Duration", type: "text", editable: false },
        ],
        technical: [
          { key: "resolution", value: "", label: "Resolution", type: "text", editable: false },
          { key: "frameRate", value: "", label: "Frame Rate", type: "text", editable: false },
          { key: "bitrate", value: "", label: "Bitrate", type: "text", editable: false },
          { key: "codec", value: "", label: "Codec", type: "text", editable: false },
          { key: "audioCodec", value: "", label: "Audio Codec", type: "text", editable: false },
          { key: "channels", value: "", label: "Audio Channels", type: "text", editable: false },
        ]
      }
    },
    document: {
      title: "Document Template",
      description: "Document metadata with author and content information",
      icon: "FileText",
      fields: {
        basic: [
          { key: "title", value: "", label: "Title", type: "text", editable: true, required: true },
          { key: "author", value: "", label: "Author", type: "text", editable: true, required: true },
          { key: "subject", value: "", label: "Subject", type: "text", editable: true },
          { key: "keywords", value: "", label: "Keywords", type: "text", editable: true },
          { key: "description", value: "", label: "Description", type: "textarea", editable: true },
          { key: "category", value: "", label: "Category", type: "text", editable: true },
          { key: "version", value: "", label: "Version", type: "text", editable: true },
          { key: "status", value: "", label: "Status", type: "select", editable: true, options: ["Draft", "Review", "Final", "Archived"] },
        ],
        technical: [
          { key: "pageCount", value: "", label: "Page Count", type: "number", editable: false },
          { key: "wordCount", value: "", label: "Word Count", type: "number", editable: false },
          { key: "characterCount", value: "", label: "Character Count", type: "number", editable: false },
          { key: "language", value: "", label: "Language", type: "text", editable: true },
          { key: "createdDate", value: "", label: "Created Date", type: "date", editable: true },
          { key: "modifiedDate", value: "", label: "Modified Date", type: "date", editable: false },
        ]
      }
    },
    custom: {
      title: "Custom Template",
      description: "Create your own custom metadata fields",
      icon: "Settings",
      fields: {}
    }
  };

  // Utility functions
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const generateFileHash = async (file: File): Promise<string> => {
    const buffer = await file.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  };

  const validateMetadata = (metadata: MetadataData): ValidationResult => {
    const errors: Array<{ field: string; message: string; severity: 'error' | 'warning' | 'info' }> = [];
    const warnings: Array<{ field: string; message: string }> = [];

    // Check required fields
    Object.keys(metadata).forEach(category => {
      if (Array.isArray(metadata[category])) {
        (metadata[category] as MetadataField[]).forEach(field => {
          if (field.required && !field.value.trim()) {
            errors.push({
              field: field.key,
              message: `${field.label} is required`,
              severity: 'error'
            });
          }

          // Validate field values
          if (field.validation) {
            const { min, max, pattern, message } = field.validation;
            if (field.value) {
              if (min !== undefined && field.value.length < min) {
                errors.push({
                  field: field.key,
                  message: message || `${field.label} must be at least ${min} characters`,
                  severity: 'error'
                });
              }
              if (max !== undefined && field.value.length > max) {
                errors.push({
                  field: field.key,
                  message: message || `${field.label} must be no more than ${max} characters`,
                  severity: 'error'
                });
              }
              if (pattern && !new RegExp(pattern).test(field.value)) {
                errors.push({
                  field: field.key,
                  message: message || `${field.label} format is invalid`,
                  severity: 'error'
                });
              }
            }
          }
        });
      }
    });

    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  };

  const calculateMetadataStats = (files: BatchFile[]): MetadataStats => {
    const totalFiles = files.length;
    const totalSize = files.reduce((sum, file) => sum + file.file.size, 0);
    const fileTypes: Record<string, number> = {};
    const commonTags: Record<string, number> = {};

    files.forEach(file => {
      const type = file.file.type.split('/')[0];
      fileTypes[type] = (fileTypes[type] || 0) + 1;

      if (file.metadata) {
        Object.keys(file.metadata).forEach(category => {
          if (Array.isArray(file.metadata![category])) {
            (file.metadata![category] as MetadataField[]).forEach(field => {
              if (field.key === 'keywords' && field.value) {
                field.value.split(',').forEach(keyword => {
                  const tag = keyword.trim().toLowerCase();
                  if (tag) {
                    commonTags[tag] = (commonTags[tag] || 0) + 1;
                  }
                });
              }
            });
          }
        });
      }
    });

    return {
      totalFiles,
      totalSize,
      fileTypes,
      commonTags,
      averageFileSize: totalFiles > 0 ? totalSize / totalFiles : 0,
      lastProcessed: new Date()
    };
  };

  // Add to history
  const addToHistory = useCallback((metadata: MetadataData) => {
    const historyItem: HistoryItem = {
      id: Date.now().toString(),
      fileName: metadata.fileName,
      timestamp: new Date(),
      metadata: JSON.stringify(metadata),
      fileSize: metadata.fileSize,
      fileType: metadata.fileType
    };
    setMetadataHistory(prev => [historyItem, ...prev.slice(0, 9)]);
  }, []);

  // Real metadata extraction
  const extractImageMetadata = async (file: File): Promise<MetadataData> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          canvas.width = img.width;
          canvas.height = img.height;
          ctx?.drawImage(img, 0, 0);

          const template = metadataTemplates.photography;
          const metadata: MetadataData = {
            fileName: file.name,
            fileSize: formatFileSize(file.size),
            fileType: file.type,
            lastModified: new Date(file.lastModified).toLocaleString(),
            fileHash: '',
            ...template.fields
          };

          // Populate with real data
          Object.keys(metadata).forEach(category => {
            if (Array.isArray(metadata[category])) {
              (metadata[category] as MetadataField[]).forEach(field => {
                if (field.key === 'title') field.value = file.name.replace(/\.[^/.]+$/, "");
                if (field.key === 'width') field.value = img.width.toString();
                if (field.key === 'height') field.value = img.height.toString();
                if (field.key === 'dateTaken') field.value = new Date().toISOString().split('T')[0];
                if (field.key === 'orientation') {
                  field.value = img.width > img.height ? 'Horizontal' : img.height > img.width ? 'Vertical' : 'Square';
                }
              });
            }
          });

          resolve(metadata);
        };
        img.src = e.target?.result as string;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const extractAudioMetadata = async (file: File): Promise<MetadataData> => {
    // For now, return mock data. In a real implementation, you'd use music-metadata library
    const template = metadataTemplates.music;
    const metadata: MetadataData = {
      fileName: file.name,
      fileSize: formatFileSize(file.size),
      fileType: file.type,
      lastModified: new Date(file.lastModified).toLocaleString(),
      fileHash: '',
      ...template.fields
    };

    // Populate with mock data
    Object.keys(metadata).forEach(category => {
      if (Array.isArray(metadata[category])) {
        (metadata[category] as MetadataField[]).forEach(field => {
          if (field.key === 'title') field.value = file.name.replace(/\.[^/.]+$/, "");
          if (field.key === 'year') field.value = new Date().getFullYear().toString();
          if (field.key === 'bitrate') field.value = '320 kbps';
          if (field.key === 'sampleRate') field.value = '44.1 kHz';
          if (field.key === 'duration') field.value = '3:45';
          if (field.key === 'format') field.value = file.type.split('/')[1].toUpperCase();
        });
      }
    });

    return metadata;
  };

  // Handle file selection
  const handleFileSelect = useCallback(async (file: File) => {
    setIsLoading(true);
    setError("");
    setSelectedFile(file);
    setIsEditing(false);

    try {
      // Check if file type is supported
      if (!metadataService.isSupported(file)) {
        setError("Unsupported file type. Please select a supported image, audio, or video file.");
        return;
      }

      // Use the metadata service to read metadata
      const result = await metadataService.readMetadata(file);
      
      setMetadata(result.metadata);
      setValidationResult(result.validation);
      setCurrentAdapter(result.adapter);
      
      // Add to history
      const historyItem: HistoryItem = {
        id: Date.now().toString(),
        fileName: file.name,
        timestamp: new Date(),
        metadata: JSON.stringify(result.metadata),
        fileSize: formatFileSize(file.size),
        fileType: file.type
      };
      setMetadataHistory(prev => [historyItem, ...prev.slice(0, 9)]);
      
    } catch (error) {
      setError(`Failed to extract metadata from file: ${error}`);
      console.error('Metadata extraction error:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Handle file input change
  const handleFileInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  }, [handleFileSelect]);

  // Handle batch file input change
  const handleBatchFileInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length > 0) {
      const batchFiles: BatchFile[] = files.map(file => ({
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
        file,
        metadata: null,
        status: 'pending' as const
      }));
      setBatchFiles(batchFiles);
      setIsBatchMode(true);
    }
  }, []);

  // Process batch files
  const processBatchFiles = useCallback(async () => {
    setIsLoading(true);
    setError("");

    for (let i = 0; i < batchFiles.length; i++) {
      const batchFile = batchFiles[i];
      setBatchFiles(prev => prev.map(bf => 
        bf.id === batchFile.id ? { ...bf, status: 'processing' } : bf
      ));

      try {
        const isImage = batchFile.file.type.startsWith('image/');
        const isAudio = batchFile.file.type.startsWith('audio/');
        const isVideo = batchFile.file.type.startsWith('video/');
        const isDocument = batchFile.file.type.includes('document') || batchFile.file.type.includes('pdf') || batchFile.file.type.includes('text');

        if (!isImage && !isAudio && !isVideo && !isDocument) {
          setBatchFiles(prev => prev.map(bf => 
            bf.id === batchFile.id ? { ...bf, status: 'error', error: 'Unsupported file type' } : bf
          ));
          continue;
        }

        let extractedMetadata: MetadataData;

        if (isImage) {
          extractedMetadata = await extractImageMetadata(batchFile.file);
        } else if (isAudio) {
          extractedMetadata = await extractAudioMetadata(batchFile.file);
        } else {
          const template = isVideo ? metadataTemplates.video : metadataTemplates.document;
          extractedMetadata = {
            fileName: batchFile.file.name,
            fileSize: formatFileSize(batchFile.file.size),
            fileType: batchFile.file.type,
            lastModified: new Date(batchFile.file.lastModified).toLocaleString(),
            fileHash: await generateFileHash(batchFile.file),
            ...template.fields
          };

          Object.keys(extractedMetadata).forEach(category => {
            if (Array.isArray(extractedMetadata[category])) {
              (extractedMetadata[category] as MetadataField[]).forEach(field => {
                if (field.key === 'title') field.value = batchFile.file.name.replace(/\.[^/.]+$/, "");
                if (field.key === 'createdDate') field.value = new Date().toISOString().split('T')[0];
                if (field.key === 'modifiedDate') field.value = new Date(batchFile.file.lastModified).toISOString().split('T')[0];
              });
            }
          });
        }

        extractedMetadata.fileHash = await generateFileHash(batchFile.file);

        setBatchFiles(prev => prev.map(bf => 
          bf.id === batchFile.id ? { ...bf, metadata: extractedMetadata, status: 'completed' } : bf
        ));

        addToHistory(extractedMetadata);
      } catch (error) {
        setBatchFiles(prev => prev.map(bf => 
          bf.id === batchFile.id ? { ...bf, status: 'error', error: 'Failed to extract metadata' } : bf
        ));
      }
    }

    // Calculate stats
    const stats = calculateMetadataStats(batchFiles);
    setMetadataStats(stats);

    setIsLoading(false);
  }, [batchFiles, addToHistory]);

  // Update metadata field
  const updateField = useCallback((field: keyof CanonicalMetadata, value: any) => {
    if (!metadata) return;

    setMetadata((prev: CanonicalMetadata | null) => {
      if (!prev) return prev;
      
      // Handle nested object updates (like gps, custom, etc.)
      if (field === 'gps' && typeof value === 'object') {
        return {
          ...prev,
          gps: {
            ...prev.gps,
            ...value
          }
        };
      }
      
      if (field === 'custom' && typeof value === 'object') {
        return {
          ...prev,
          custom: {
            ...prev.custom,
            ...value
          }
        };
      }
      
      return {
        ...prev,
        [field]: value
      };
    });
  }, [metadata]);

  // Load from history
  const loadFromHistory = useCallback((historyItem: HistoryItem) => {
    try {
      const parsedMetadata = JSON.parse(historyItem.metadata);
      setMetadata(parsedMetadata);
      setIsEditing(false);
    } catch (error) {
      setError("Failed to load metadata from history.");
    }
  }, []);

  // Toggle favorite
  const toggleFavorite = useCallback((fileName: string) => {
    setFavorites(prev => {
      if (prev.includes(fileName)) {
        return prev.filter(name => name !== fileName);
      } else {
        return [...prev, fileName];
      }
    });
  }, []);

  // Save metadata
  const saveMetadata = useCallback(async () => {
    if (!metadata || !selectedFile) return;

    setIsLoading(true);
    setError("");

    try {
      // Validate before saving
      const validation = validateCanonicalMetadata(metadata);
      if (!validation.isValid) {
        setValidationResult(validation);
        setError("Please fix validation errors before saving.");
        return;
      }

      // Use the metadata service to write metadata
      const updatedFile = await metadataService.writeMetadata(selectedFile, metadata);
      
      setIsEditing(false);
      setValidationResult(null);
      
      // Update the selected file with the new one
      setSelectedFile(updatedFile);
      
      // Add to history
      const historyItem: HistoryItem = {
        id: Date.now().toString(),
        fileName: updatedFile.name,
        timestamp: new Date(),
        metadata: JSON.stringify(metadata),
        fileSize: formatFileSize(updatedFile.size),
        fileType: updatedFile.type
      };
      setMetadataHistory(prev => [historyItem, ...prev.slice(0, 9)]);
      
    } catch (error) {
      setError(`Failed to save metadata: ${error}`);
      console.error('Metadata save error:', error);
    } finally {
      setIsLoading(false);
    }
  }, [metadata, selectedFile]);

  // Export metadata
  const exportMetadata = useCallback((format: 'json' | 'csv' | 'xml' | 'yaml') => {
    if (!metadata || !selectedFile) return;

    try {
      const content = metadataService.exportMetadata(metadata, format);
      const filename = `${selectedFile.name.replace(/\.[^/.]+$/, "")}_metadata.${format}`;
      
      let mimeType = '';
      switch (format) {
        case 'json':
          mimeType = 'application/json';
          break;
        case 'csv':
          mimeType = 'text/csv';
          break;
        case 'xml':
          mimeType = 'application/xml';
          break;
        case 'yaml':
          mimeType = 'text/yaml';
          break;
      }

      const blob = new Blob([content], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      setError(`Failed to export metadata: ${error}`);
    }
  }, [metadata, selectedFile]);

  // Import metadata
  const importMetadata = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        const extension = file.name.split('.').pop()?.toLowerCase();
        
        let format: 'json' | 'csv' | 'xml' | 'yaml' = 'json';
        switch (extension) {
          case 'csv':
            format = 'csv';
            break;
          case 'xml':
            format = 'xml';
            break;
          case 'yaml':
          case 'yml':
            format = 'yaml';
            break;
          default:
            format = 'json';
        }
        
        const importedMetadata = metadataService.importMetadata(content, format);
        setMetadata(importedMetadata);
        setIsEditing(true);
        setError("");
        
        // Validate imported metadata
        const validation = validateCanonicalMetadata(importedMetadata);
        setValidationResult(validation);
      } catch (error) {
        setError(`Failed to import metadata: ${error}`);
      }
    };
    reader.readAsText(file);
  }, []);

  // Add custom field
  const addCustomField = useCallback((category: string) => {
    const newField: MetadataField = {
      key: `custom_${Date.now()}`,
      value: '',
      label: 'New Field',
      type: 'text',
      editable: true,
      category,
      description: 'Custom field'
    };
    setCustomFields(prev => [...prev, newField]);
  }, []);

  // Remove custom field
  const removeCustomField = useCallback((fieldKey: string) => {
    setCustomFields(prev => prev.filter(field => field.key !== fieldKey));
  }, []);

  // Apply template
  const applyTemplate = useCallback((templateKey: string) => {
    if (templateKey === 'auto') {
      // Auto-detect based on file type
      if (selectedFile) {
        const isImage = selectedFile.type.startsWith('image/');
        const isAudio = selectedFile.type.startsWith('audio/');
        const isVideo = selectedFile.type.startsWith('video/');
        const isDocument = selectedFile.type.includes('document') || selectedFile.type.includes('pdf') || selectedFile.type.includes('text');
        
        if (isImage) {
          setSelectedTemplate('photography');
        } else if (isAudio) {
          setSelectedTemplate('music');
        } else if (isVideo) {
          setSelectedTemplate('video');
        } else if (isDocument) {
          setSelectedTemplate('document');
        }
      }
    } else {
      setSelectedTemplate(templateKey);
    }
  }, [selectedFile]);

  // Download metadata as JSON
  const downloadMetadata = useCallback(() => {
    if (!metadata || !selectedFile) return;

    const metadataJson = {
      fileName: selectedFile.name,
      fileSize: formatFileSize(selectedFile.size),
      fileType: selectedFile.type,
      lastModified: new Date(selectedFile.lastModified).toLocaleString(),
      metadata: metadata
    };

    const blob = new Blob([JSON.stringify(metadataJson, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedFile.name.replace(/\.[^/.]+$/, "")}_metadata.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [metadata, selectedFile]);

  // Filter metadata fields
  const filteredMetadata = useMemo(() => {
    if (!metadata) return null;

    // For canonical metadata, we'll filter based on field names and values
    const filtered: Partial<CanonicalMetadata> = {};
    
    Object.entries(metadata).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        const matchesSearch = searchTerm === "" || 
          key.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (typeof value === 'string' && value.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (Array.isArray(value) && value.some(item => 
            typeof item === 'string' && item.toLowerCase().includes(searchTerm.toLowerCase())
          ));
        
        const matchesCategory = filterCategory === "all" || getFieldCategory(key) === filterCategory;
        
        if (matchesSearch && matchesCategory) {
          (filtered as any)[key] = value;
        }
      }
    });

    return filtered;
  }, [metadata, searchTerm, filterCategory]);

  return (
    <div className="container mx-auto px-4 py-8 animate-fade-in">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold mb-4 animate-slide-in-left">Advanced Metadata Editor</h1>
          <p className="text-muted-foreground text-lg animate-slide-in-right">
            Professional metadata editing with advanced features, templates, and batch processing
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* File Upload & Info */}
          <div className="lg:col-span-1 space-y-6">
            <Card className="card-enhanced">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Upload className="h-5 w-5" />
                  <span>File Upload</span>
                </CardTitle>
                <CardDescription>
                  Select an image or audio file to edit metadata
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="border-2 border-dashed border-muted rounded-lg p-6 text-center">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,audio/*"
                    onChange={handleFileInputChange}
                    className="hidden"
                  />
                  <Button
                    onClick={() => fileInputRef.current?.click()}
                    variant="outline"
                    className="w-full hover-lift"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" />
                        Choose File
                      </>
                    )}
                  </Button>
                  <p className="text-sm text-muted-foreground mt-2">
                    Supports: JPG, PNG, GIF, MP3, WAV, FLAC
                  </p>
                </div>

                {selectedFile && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      {selectedFile.type.startsWith('image/') ? (
                        <FileImage className="h-5 w-5 text-blue-500" />
                      ) : (
                        <Music className="h-5 w-5 text-green-500" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{selectedFile.name}</p>
                        <p className="text-sm text-muted-foreground">{formatFileSize(selectedFile.size)}</p>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Button
                        onClick={() => setIsEditing(!isEditing)}
                        variant={isEditing ? "default" : "outline"}
                        size="sm"
                        className="flex-1"
                      >
                        {isEditing ? (
                          <>
                            <Eye className="h-4 w-4 mr-2" />
                            View
                          </>
                        ) : (
                          <>
                            <Edit className="h-4 w-4 mr-2" />
                            Edit
                          </>
                        )}
                      </Button>
                      <Button
                        onClick={() => setShowAdvanced(!showAdvanced)}
                        variant="outline"
                        size="sm"
                        className="hover-lift"
                      >
                        <Settings className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}

                {error && (
                  <Alert className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950">
                    <AlertTriangle className="h-4 w-4 text-red-600" />
                    <AlertDescription className="text-red-800 dark:text-red-200">
                      {error}
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>

            {/* File Info */}
            {selectedFile && (
              <Card className="card-enhanced">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Info className="h-5 w-5" />
                    <span>File Information</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-muted-foreground">Name:</span>
                      <p className="font-medium truncate">{selectedFile.name}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Size:</span>
                      <p className="font-medium">{formatFileSize(selectedFile.size)}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Type:</span>
                      <p className="font-medium">{selectedFile.type}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Modified:</span>
                      <p className="font-medium">{new Date(selectedFile.lastModified).toLocaleString()}</p>
                    </div>
                  </div>
                  {currentAdapter && (
                    <div className="pt-2 border-t">
                      <span className="text-muted-foreground">Adapter:</span>
                      <p className="font-medium text-sm">{currentAdapter}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Metadata History */}
            {metadataHistory.length > 0 && (
              <Card className="card-enhanced">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <HistoryIcon className="h-5 w-5" />
                    <span>Recent Files</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {metadataHistory.slice(0, 5).map((item) => (
                      <div
                        key={item.id}
                        className="flex items-center justify-between p-2 bg-muted/50 rounded-lg hover:bg-muted/70 transition-colors cursor-pointer"
                        onClick={() => loadFromHistory(item)}
                      >
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate text-sm">{item.fileName}</p>
                          <p className="text-xs text-muted-foreground">
                            {item.timestamp.toLocaleDateString()}
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleFavorite(item.fileName);
                          }}
                        >
                          <Star className={`h-3 w-3 ${favorites.includes(item.fileName) ? 'fill-yellow-400 text-yellow-400' : ''}`} />
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Metadata Editor */}
          <div className="lg:col-span-3">
            {metadata ? (
              <Card className="card-enhanced">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Edit className="h-5 w-5" />
                    <span>Metadata Editor</span>
                    {isEditing && (
                      <Badge variant="secondary">Editing</Badge>
                    )}
                  </CardTitle>
                  <CardDescription>
                    {isEditing ? 'Edit metadata fields below' : 'View metadata information'}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {/* Search and Filter */}
                  <div className="flex gap-4 mb-6">
                    <div className="flex-1">
                      <Label>Search Fields</Label>
                      <Input
                        placeholder="Search metadata fields..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="mt-1"
                      />
                    </div>
                    <div className="w-48">
                      <Label>Filter Category</Label>
                      <Select value={filterCategory} onValueChange={setFilterCategory}>
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Categories</SelectItem>
                          <SelectItem value="basic">Basic Info</SelectItem>
                          <SelectItem value="camera">Camera</SelectItem>
                          <SelectItem value="location">Location</SelectItem>
                          <SelectItem value="technical">Technical</SelectItem>
                          <SelectItem value="audio">Audio</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                    <TabsList className="grid w-full grid-cols-4">
                      <TabsTrigger value="basic">Basic</TabsTrigger>
                      <TabsTrigger value="camera">Camera</TabsTrigger>
                      <TabsTrigger value="location">Location</TabsTrigger>
                      <TabsTrigger value="technical">Technical</TabsTrigger>
                    </TabsList>

                    {filteredMetadata && (
                      <>
                        <TabsContent value="basic" className="space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {filteredMetadata.title !== undefined && (
                              <div className="space-y-2">
                                <Label className="flex items-center gap-2">
                                  <Tag className="h-4 w-4" />
                                  Title
                                </Label>
                                <Input
                                  value={filteredMetadata.title || ''}
                                  onChange={(e) => updateField('title', e.target.value)}
                                  placeholder="Enter title"
                                />
                              </div>
                            )}
                            {filteredMetadata.description !== undefined && (
                              <div className="space-y-2">
                                <Label className="flex items-center gap-2">
                                  <FileText className="h-4 w-4" />
                                  Description
                                </Label>
                                <Textarea
                                  value={filteredMetadata.description || ''}
                                  onChange={(e) => updateField('description', e.target.value)}
                                  placeholder="Enter description"
                                  rows={3}
                                />
                              </div>
                            )}
                            {filteredMetadata.creator !== undefined && (
                              <div className="space-y-2">
                                <Label className="flex items-center gap-2">
                                  <User className="h-4 w-4" />
                                  Creator
                                </Label>
                                <Input
                                  value={filteredMetadata.creator || ''}
                                  onChange={(e) => updateField('creator', e.target.value)}
                                  placeholder="Enter creator"
                                />
                              </div>
                            )}
                            {filteredMetadata.copyright !== undefined && (
                              <div className="space-y-2">
                                <Label className="flex items-center gap-2">
                                  <Shield className="h-4 w-4" />
                                  Copyright
                                </Label>
                                <Input
                                  value={filteredMetadata.copyright || ''}
                                  onChange={(e) => updateField('copyright', e.target.value)}
                                  placeholder="Enter copyright"
                                />
                              </div>
                            )}
                            {filteredMetadata.keywords !== undefined && (
                              <div className="space-y-2">
                                <Label className="flex items-center gap-2">
                                  <Tag className="h-4 w-4" />
                                  Keywords
                                </Label>
                                <Input
                                  value={Array.isArray(filteredMetadata.keywords) ? filteredMetadata.keywords.join(', ') : ''}
                                  onChange={(e) => updateField('keywords', e.target.value.split(',').map(k => k.trim()).filter(k => k))}
                                  placeholder="Enter keywords (comma-separated)"
                                />
                              </div>
                            )}
                            {filteredMetadata.rating !== undefined && (
                              <div className="space-y-2">
                                <Label className="flex items-center gap-2">
                                  <Star className="h-4 w-4" />
                                  Rating
                                </Label>
                                <Select value={filteredMetadata.rating?.toString() || '0'} onValueChange={(value) => updateField('rating', parseInt(value))}>
                                  <SelectTrigger>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="0">No Rating</SelectItem>
                                    <SelectItem value="1">1 Star</SelectItem>
                                    <SelectItem value="2">2 Stars</SelectItem>
                                    <SelectItem value="3">3 Stars</SelectItem>
                                    <SelectItem value="4">4 Stars</SelectItem>
                                    <SelectItem value="5">5 Stars</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            )}
                            {filteredMetadata.people !== undefined && (
                              <div className="space-y-2">
                                <Label className="flex items-center gap-2">
                                  <User className="h-4 w-4" />
                                  People
                                </Label>
                                <Input
                                  value={Array.isArray(filteredMetadata.people) ? filteredMetadata.people.join(', ') : ''}
                                  onChange={(e) => updateField('people', e.target.value.split(',').map(p => p.trim()).filter(p => p))}
                                  placeholder="Enter people (comma-separated)"
                                />
                              </div>
                            )}
                            <div className="space-y-2">
                              <Label className="flex items-center gap-2">
                                <Globe className="h-4 w-4" />
                                Language
                              </Label>
                              <Input
                                value={filteredMetadata.language || ''}
                                onChange={(e) => updateField('language', e.target.value)}
                                placeholder="Enter language (e.g., en, es, fr)"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label className="flex items-center gap-2">
                                <Globe className="h-4 w-4" />
                                Locale
                              </Label>
                              <Input
                                value={filteredMetadata.locale || ''}
                                onChange={(e) => updateField('locale', e.target.value)}
                                placeholder="Enter locale (e.g., en_US, es_ES)"
                              />
                            </div>
                          </div>
                        </TabsContent>
                        
                        <TabsContent value="camera" className="space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {filteredMetadata.technical && (
                              <>
                                {filteredMetadata.technical.resolution && (
                                  <div className="space-y-2">
                                    <Label className="flex items-center gap-2">
                                      <Camera className="h-4 w-4" />
                                      Resolution
                                    </Label>
                                    <Input
                                      value={`${filteredMetadata.technical.resolution.width} × ${filteredMetadata.technical.resolution.height}`}
                                      disabled
                                      className="bg-muted"
                                    />
                                  </div>
                                )}
                                {filteredMetadata.technical.colorSpace && (
                                  <div className="space-y-2">
                                    <Label className="flex items-center gap-2">
                                      <Settings className="h-4 w-4" />
                                      Color Space
                                    </Label>
                                    <Input
                                      value={filteredMetadata.technical.colorSpace}
                                      disabled
                                      className="bg-muted"
                                    />
                                  </div>
                                )}
                                {filteredMetadata.technical.colorDepth && (
                                  <div className="space-y-2">
                                    <Label className="flex items-center gap-2">
                                      <BarChart3 className="h-4 w-4" />
                                      Color Depth
                                    </Label>
                                    <Input
                                      value={`${filteredMetadata.technical.colorDepth} bits`}
                                      disabled
                                      className="bg-muted"
                                    />
                                  </div>
                                )}
                              </>
                            )}
                            <div className="space-y-2">
                              <Label className="flex items-center gap-2">
                                <Camera className="h-4 w-4" />
                                Camera Make
                              </Label>
                              <Input
                                placeholder="Enter camera make"
                                onChange={(e) => updateField('custom', { ...(filteredMetadata.custom || {}), cameraMake: e.target.value })}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label className="flex items-center gap-2">
                                <Camera className="h-4 w-4" />
                                Camera Model
                              </Label>
                              <Input
                                placeholder="Enter camera model"
                                onChange={(e) => updateField('custom', { ...(filteredMetadata.custom || {}), cameraModel: e.target.value })}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label className="flex items-center gap-2">
                                <Settings className="h-4 w-4" />
                                Lens
                              </Label>
                              <Input
                                placeholder="Enter lens information"
                                onChange={(e) => updateField('custom', { ...(filteredMetadata.custom || {}), lens: e.target.value })}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label className="flex items-center gap-2">
                                <Hash className="h-4 w-4" />
                                ISO
                              </Label>
                              <Input
                                type="number"
                                placeholder="Enter ISO value"
                                onChange={(e) => updateField('custom', { ...(filteredMetadata.custom || {}), iso: e.target.value })}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label className="flex items-center gap-2">
                                <Clock className="h-4 w-4" />
                                Shutter Speed
                              </Label>
                              <Input
                                placeholder="Enter shutter speed"
                                onChange={(e) => updateField('custom', { ...(filteredMetadata.custom || {}), shutterSpeed: e.target.value })}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label className="flex items-center gap-2">
                                <Settings className="h-4 w-4" />
                                Aperture
                              </Label>
                              <Input
                                placeholder="Enter aperture (f-stop)"
                                onChange={(e) => updateField('custom', { ...(filteredMetadata.custom || {}), aperture: e.target.value })}
                              />
                            </div>
                          </div>
                        </TabsContent>
                        
                        <TabsContent value="location" className="space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {filteredMetadata.gps && (
                              <>
                                {filteredMetadata.gps.latitude !== undefined && (
                                  <div className="space-y-2">
                                    <Label className="flex items-center gap-2">
                                      <MapPin className="h-4 w-4" />
                                      Latitude
                                    </Label>
                                    <Input
                                      value={filteredMetadata.gps.latitude.toString()}
                                      onChange={(e) => updateField('gps', { ...filteredMetadata.gps, latitude: parseFloat(e.target.value) })}
                                      placeholder="Enter latitude"
                                    />
                                  </div>
                                )}
                                {filteredMetadata.gps.longitude !== undefined && (
                                  <div className="space-y-2">
                                    <Label className="flex items-center gap-2">
                                      <MapPin className="h-4 w-4" />
                                      Longitude
                                    </Label>
                                    <Input
                                      value={filteredMetadata.gps.longitude.toString()}
                                      onChange={(e) => updateField('gps', { ...filteredMetadata.gps, longitude: parseFloat(e.target.value) })}
                                      placeholder="Enter longitude"
                                    />
                                  </div>
                                )}
                                {filteredMetadata.gps.altitude !== undefined && (
                                  <div className="space-y-2">
                                    <Label className="flex items-center gap-2">
                                      <BarChart3 className="h-4 w-4" />
                                      Altitude
                                    </Label>
                                    <Input
                                      value={filteredMetadata.gps.altitude.toString()}
                                      onChange={(e) => updateField('gps', { ...filteredMetadata.gps, altitude: parseFloat(e.target.value) })}
                                      placeholder="Enter altitude"
                                    />
                                  </div>
                                )}
                              </>
                            )}
                            <div className="space-y-2">
                              <Label className="flex items-center gap-2">
                                <MapPin className="h-4 w-4" />
                                Location
                              </Label>
                              <Input
                                placeholder="Enter location name"
                                onChange={(e) => updateField('gps', { ...(filteredMetadata.gps || {}), location: e.target.value })}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label className="flex items-center gap-2">
                                <MapPin className="h-4 w-4" />
                                City
                              </Label>
                              <Input
                                placeholder="Enter city"
                                onChange={(e) => updateField('gps', { ...(filteredMetadata.gps || {}), city: e.target.value })}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label className="flex items-center gap-2">
                                <MapPin className="h-4 w-4" />
                                State/Province
                              </Label>
                              <Input
                                placeholder="Enter state or province"
                                onChange={(e) => updateField('gps', { ...(filteredMetadata.gps || {}), state: e.target.value })}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label className="flex items-center gap-2">
                                <Globe className="h-4 w-4" />
                                Country
                              </Label>
                              <Input
                                placeholder="Enter country"
                                onChange={(e) => updateField('gps', { ...(filteredMetadata.gps || {}), country: e.target.value })}
                              />
                            </div>
                          </div>
                        </TabsContent>
                        
                        <TabsContent value="technical" className="space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {filteredMetadata.technical && (
                              <>
                                {filteredMetadata.technical.resolution && (
                                  <div className="space-y-2">
                                    <Label className="flex items-center gap-2">
                                      <BarChart3 className="h-4 w-4" />
                                      Resolution
                                    </Label>
                                    <Input
                                      value={`${filteredMetadata.technical.resolution.width} × ${filteredMetadata.technical.resolution.height}`}
                                      disabled
                                      className="bg-muted"
                                    />
                                  </div>
                                )}
                                {filteredMetadata.technical.duration && (
                                  <div className="space-y-2">
                                    <Label className="flex items-center gap-2">
                                      <Clock className="h-4 w-4" />
                                      Duration
                                    </Label>
                                    <Input
                                      value={`${Math.floor(filteredMetadata.technical.duration / 60)}:${(filteredMetadata.technical.duration % 60).toFixed(0).padStart(2, '0')}`}
                                      disabled
                                      className="bg-muted"
                                    />
                                  </div>
                                )}
                                {filteredMetadata.technical.bitrate && (
                                  <div className="space-y-2">
                                    <Label className="flex items-center gap-2">
                                      <Activity className="h-4 w-4" />
                                      Bitrate
                                    </Label>
                                    <Input
                                      value={`${Math.round(filteredMetadata.technical.bitrate / 1000)} kbps`}
                                      disabled
                                      className="bg-muted"
                                    />
                                  </div>
                                )}
                                {filteredMetadata.technical.codec && (
                                  <div className="space-y-2">
                                    <Label className="flex items-center gap-2">
                                      <Settings className="h-4 w-4" />
                                      Codec
                                    </Label>
                                    <Input
                                      value={filteredMetadata.technical.codec}
                                      disabled
                                      className="bg-muted"
                                    />
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        </TabsContent>
                      </>
                    )}
                  </Tabs>

                  {isEditing && (
                    <div className="flex gap-2 mt-6 pt-6 border-t">
                      <Button
                        onClick={saveMetadata}
                        disabled={isLoading}
                        className="flex-1"
                      >
                        {isLoading ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                            Saving...
                          </>
                        ) : (
                          <>
                            <Save className="h-4 w-4 mr-2" />
                            Save Changes
                          </>
                        )}
                      </Button>
                      <Button
                        onClick={() => setIsEditing(false)}
                        variant="outline"
                      >
                        <X className="h-4 w-4 mr-2" />
                        Cancel
                      </Button>
                    </div>
                  )}

                  {!isEditing && (
                    <div className="flex gap-2 mt-6 pt-6 border-t">
                      <Button
                        onClick={downloadMetadata}
                        variant="outline"
                        className="flex-1"
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Export JSON
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Card className="card-enhanced">
                <CardContent className="text-center py-12">
                  <FileImage className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-xl font-semibold mb-2">No File Selected</h3>
                  <p className="text-muted-foreground mb-4">
                    Upload an image or audio file to view and edit its metadata
                  </p>
                  <Button
                    onClick={() => fileInputRef.current?.click()}
                    variant="outline"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Choose File
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Advanced Features Panel */}
        {showAdvanced && (
          <Card className="card-enhanced mt-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="h-5 w-5" />
                <span>Advanced Features</span>
              </CardTitle>
              <CardDescription>
                Professional metadata editing tools and batch processing
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-4">
                  <h4 className="font-semibold">Metadata Templates</h4>
                  <div className="space-y-2">
                    <Button variant="outline" className="w-full justify-start">
                      <Camera className="h-4 w-4 mr-2" />
                      Photography Template
                    </Button>
                    <Button variant="outline" className="w-full justify-start">
                      <Music className="h-4 w-4 mr-2" />
                      Music Template
                    </Button>
                    <Button variant="outline" className="w-full justify-start">
                      <FileText className="h-4 w-4 mr-2" />
                      Custom Template
                    </Button>
                  </div>
                </div>
                <div className="space-y-4">
                  <h4 className="font-semibold">Batch Processing</h4>
                  <div className="space-y-2">
                    <Button variant="outline" className="w-full justify-start">
                      <Upload className="h-4 w-4 mr-2" />
                      Batch Upload
                    </Button>
                    <Button variant="outline" className="w-full justify-start">
                      <Download className="h-4 w-4 mr-2" />
                      Batch Export
                    </Button>
                    <Button variant="outline" className="w-full justify-start">
                      <Settings className="h-4 w-4 mr-2" />
                      Apply Template
                    </Button>
                  </div>
                </div>
                <div className="space-y-4">
                  <h4 className="font-semibold">Analysis Tools</h4>
                  <div className="space-y-2">
                    <Button variant="outline" className="w-full justify-start">
                      <BarChart3 className="h-4 w-4 mr-2" />
                      Metadata Stats
                    </Button>
                    <Button variant="outline" className="w-full justify-start">
                      <Search className="h-4 w-4 mr-2" />
                      Duplicate Finder
                    </Button>
                    <Button variant="outline" className="w-full justify-start">
                      <Shield className="h-4 w-4 mr-2" />
                      Privacy Check
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Info Card */}
        <Card className="card-enhanced mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Info className="h-5 w-5" />
              Advanced Metadata Features
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <h3 className="font-semibold">Professional Features</h3>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Advanced search and filtering capabilities</li>
                  <li>• Metadata templates for different file types</li>
                  <li>• Batch processing and bulk operations</li>
                  <li>• Metadata history and favorites</li>
                  <li>• Privacy and security analysis tools</li>
                </ul>
              </div>
              <div className="space-y-2">
                <h3 className="font-semibold">Supported Formats</h3>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Images: JPEG, PNG, GIF, TIFF, WebP</li>
                  <li>• Audio: MP3, WAV, FLAC, AAC, OGG</li>
                  <li>• Video: MP4, AVI, MOV, MKV</li>
                  <li>• Documents: PDF, DOC, DOCX</li>
                  <li>• Archives: ZIP, RAR, 7Z</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

